import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { authApi, handleAuthError, UserInfo } from '../api/authApi';
import { 
  getDeviceId, 
  setDeviceId, 
  clearDeviceId, 
  generateDeviceId,
  getSignatureString 
} from '../utils/browserFingerprint';

// Session types for different use cases
export type SessionType = 'interview' | 'practice' | 'default';

// Auth state interface
export interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: UserInfo | null;
  token: string | null;
  tokenExpiresAt: number | null; // Unix timestamp in milliseconds
  deviceId: string | null; // Unique device identifier for single-device enforcement
  sessionType: SessionType; // Type of current session
  sessionStartedAt: number | null; // When the session started
  browserSignature: string | null; // Browser fingerprint signature
  error: string | null;
  isInitialized: boolean;
  sessionInvalidatedReason: string | null; // Reason if session was invalidated
}

// Initial state
const initialState: AuthState = {
  isAuthenticated: false,
  isLoading: false,
  user: null,
  token: localStorage.getItem('authToken'),
  tokenExpiresAt: parseInt(localStorage.getItem('tokenExpiresAt') || '0') || null,
  deviceId: getDeviceId(),
  sessionType: (localStorage.getItem('sessionType') as SessionType) || 'default',
  sessionStartedAt: parseInt(localStorage.getItem('sessionStartedAt') || '0') || null,
  browserSignature: null, // Will be set on initialization
  error: null,
  isInitialized: false,
  sessionInvalidatedReason: null,
};

// Async thunks for authentication actions
export const requestVerificationCode = createAsyncThunk(
  'auth/requestVerificationCode',
  async (email: string, { rejectWithValue }) => {
    try {
      const response = await authApi.requestVerificationCode({ email });
      return response;
    } catch (error) {
      return rejectWithValue(handleAuthError(error));
    }
  }
);

export const verifyCodeAndLogin = createAsyncThunk(
  'auth/verifyCodeAndLogin',
  async ({ email, code, sessionType = 'default' }: { email: string; code: string; sessionType?: SessionType }, { rejectWithValue }) => {
    try {
      const response = await authApi.verifyCodeAndLogin({ email, code });
      
      // Store token in localStorage
      localStorage.setItem('authToken', response.access_token);
      
      // Calculate and store token expiry time (expires_in is in seconds)
      const expiresAt = Date.now() + (response.expires_in * 1000);
      localStorage.setItem('tokenExpiresAt', expiresAt.toString());
      
      // Generate and store device ID for single-device enforcement
      // If backend provides device_id in response, use that instead
      const deviceId = (response as any).device_id || generateDeviceId();
      setDeviceId(deviceId);
      
      // Store session metadata
      const sessionStartedAt = Date.now();
      localStorage.setItem('sessionType', sessionType);
      localStorage.setItem('sessionStartedAt', sessionStartedAt.toString());
      
      // Get browser signature for this session
      const browserSignature = getSignatureString();
      
      // Get user info after successful login
      const userInfo = await authApi.getUserInfo();
      
      return {
        token: response.access_token,
        user: userInfo,
        expiresIn: response.expires_in,
        expiresAt,
        deviceId,
        sessionType,
        sessionStartedAt,
        browserSignature,
      };
    } catch (error) {
      return rejectWithValue(handleAuthError(error));
    }
  }
);

export const refreshToken = createAsyncThunk(
  'auth/refreshToken',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authApi.refreshToken();
      
      // Store new token in localStorage
      localStorage.setItem('authToken', response.access_token);
      
      // Calculate and store token expiry time (expires_in is in seconds)
      const expiresAt = Date.now() + (response.expires_in * 1000);
      localStorage.setItem('tokenExpiresAt', expiresAt.toString());
      
      return {
        token: response.access_token,
        expiresIn: response.expires_in,
        expiresAt,
      };
    } catch (error) {
      // Clear token on refresh failure
      localStorage.removeItem('authToken');
      localStorage.removeItem('tokenExpiresAt');
      return rejectWithValue(handleAuthError(error));
    }
  }
);

export const getUserInfo = createAsyncThunk(
  'auth/getUserInfo',
  async (_, { rejectWithValue }) => {
    try {
      const userInfo = await authApi.getUserInfo();
      return userInfo;
    } catch (error) {
      return rejectWithValue(handleAuthError(error));
    }
  }
);

export const logout = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      await authApi.logout();
      
      // Clear all auth data from localStorage
      localStorage.removeItem('authToken');
      localStorage.removeItem('tokenExpiresAt');
      localStorage.removeItem('sessionType');
      localStorage.removeItem('sessionStartedAt');
      localStorage.removeItem('preferencesCompleted');
      localStorage.removeItem('userPreferences');
      clearDeviceId();
      
      return null;
    } catch (error) {
      // Even if logout fails on server, clear local data
      localStorage.removeItem('authToken');
      localStorage.removeItem('tokenExpiresAt');
      localStorage.removeItem('sessionType');
      localStorage.removeItem('sessionStartedAt');
      localStorage.removeItem('preferencesCompleted');
      localStorage.removeItem('userPreferences');
      clearDeviceId();
      return rejectWithValue(handleAuthError(error));
    }
  }
);

// Initialize auth state (check if user is already logged in)
export const initializeAuth = createAsyncThunk(
  'auth/initialize',
  async (_, { rejectWithValue }) => {
    const token = localStorage.getItem('authToken');
    const deviceId = getDeviceId();
    const browserSignature = getSignatureString();
    
    if (!token) {
      return { 
        isAuthenticated: false,
        browserSignature, // Always capture browser signature 
      };
    }
    
    try {
      // Verify token is still valid by getting user info
      const userInfo = await authApi.getUserInfo();
      return {
        isAuthenticated: true,
        user: userInfo,
        token,
        deviceId,
        sessionType: (localStorage.getItem('sessionType') as SessionType) || 'default',
        sessionStartedAt: parseInt(localStorage.getItem('sessionStartedAt') || '0') || null,
        browserSignature,
      };
    } catch (error) {
      // Token is invalid, clear all auth data
      localStorage.removeItem('authToken');
      localStorage.removeItem('tokenExpiresAt');
      localStorage.removeItem('sessionType');
      localStorage.removeItem('sessionStartedAt');
      clearDeviceId();
      return { 
        isAuthenticated: false,
        browserSignature,
      };
    }
  }
);

// Auth slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearAuth: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.token = null;
      state.tokenExpiresAt = null;
      state.deviceId = null;
      state.sessionType = 'default';
      state.sessionStartedAt = null;
      state.error = null;
      state.sessionInvalidatedReason = null;
      localStorage.removeItem('authToken');
      localStorage.removeItem('tokenExpiresAt');
      localStorage.removeItem('sessionType');
      localStorage.removeItem('sessionStartedAt');
      clearDeviceId();
    },
    setSessionType: (state, action: PayloadAction<SessionType>) => {
      state.sessionType = action.payload;
      localStorage.setItem('sessionType', action.payload);
    },
    setSessionInvalidated: (state, action: PayloadAction<string>) => {
      state.sessionInvalidatedReason = action.payload;
      state.isAuthenticated = false;
      state.token = null;
      state.deviceId = null;
    },
    clearSessionInvalidated: (state) => {
      state.sessionInvalidatedReason = null;
    },
  },
  extraReducers: (builder) => {
    // Request verification code
    builder
      .addCase(requestVerificationCode.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(requestVerificationCode.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(requestVerificationCode.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Verify code and login
    builder
      .addCase(verifyCodeAndLogin.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.sessionInvalidatedReason = null;
      })
      .addCase(verifyCodeAndLogin.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.tokenExpiresAt = action.payload.expiresAt;
        state.deviceId = action.payload.deviceId;
        state.sessionType = action.payload.sessionType;
        state.sessionStartedAt = action.payload.sessionStartedAt;
        state.browserSignature = action.payload.browserSignature;
        state.error = null;
        state.sessionInvalidatedReason = null;
      })
      .addCase(verifyCodeAndLogin.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.tokenExpiresAt = null;
        state.deviceId = null;
        state.sessionType = 'default';
        state.sessionStartedAt = null;
        state.error = action.payload as string;
      });

    // Refresh token
    builder
      .addCase(refreshToken.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(refreshToken.fulfilled, (state, action) => {
        state.isLoading = false;
        state.token = action.payload.token;
        state.tokenExpiresAt = action.payload.expiresAt;
        state.error = null;
      })
      .addCase(refreshToken.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.tokenExpiresAt = null;
        state.deviceId = null;
        state.sessionType = 'default';
        state.sessionStartedAt = null;
        state.error = action.payload as string;
      });

    // Get user info
    builder
      .addCase(getUserInfo.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getUserInfo.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload;
        state.error = null;
      })
      .addCase(getUserInfo.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Logout
    builder
      .addCase(logout.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logout.fulfilled, (state) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.tokenExpiresAt = null;
        state.deviceId = null;
        state.sessionType = 'default';
        state.sessionStartedAt = null;
        state.error = null;
        state.sessionInvalidatedReason = null;
      })
      .addCase(logout.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.tokenExpiresAt = null;
        state.deviceId = null;
        state.sessionType = 'default';
        state.sessionStartedAt = null;
        state.error = action.payload as string;
      });

    // Initialize auth
    builder
      .addCase(initializeAuth.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(initializeAuth.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isInitialized = true;
        state.browserSignature = action.payload.browserSignature || null;
        
        if (action.payload.isAuthenticated) {
          state.isAuthenticated = true;
          state.user = action.payload.user!;
          state.token = action.payload.token!;
          state.tokenExpiresAt = parseInt(localStorage.getItem('tokenExpiresAt') || '0') || null;
          state.deviceId = action.payload.deviceId || null;
          state.sessionType = action.payload.sessionType || 'default';
          state.sessionStartedAt = action.payload.sessionStartedAt || null;
        } else {
          state.isAuthenticated = false;
          state.user = null;
          state.token = null;
          state.tokenExpiresAt = null;
          state.deviceId = null;
          state.sessionType = 'default';
          state.sessionStartedAt = null;
        }
        state.error = null;
      })
      .addCase(initializeAuth.rejected, (state) => {
        state.isLoading = false;
        state.isInitialized = true;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.tokenExpiresAt = null;
        state.deviceId = null;
        state.sessionType = 'default';
        state.sessionStartedAt = null;
        state.error = null;
      });
  },
});

export const { 
  clearError, 
  clearAuth, 
  setSessionType, 
  setSessionInvalidated, 
  clearSessionInvalidated 
} = authSlice.actions;
export default authSlice.reducer;
